class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.initialResults = this.querySelector(
      "[data-predictive-search-initial]",
    );
    this.productsGrid = this.querySelector("#RecommendedProductsGrid");
    this.productsHeading = this.querySelector("#RecommendedProductsHeading");
    this.categoriesHeading = this.querySelector(
      "#RecommendedCategoriesHeading",
    );
    this.categoriesList = this.querySelector("#RecommendedCategoriesList");
    this.seeMoreContainer = this.querySelector("#SearchSeeMore");

    // Store initial state
    this.initialProductsHTML = this.productsGrid?.innerHTML;
    this.initialCategoriesHTML = this.categoriesList?.innerHTML;

    this.historyList = this.querySelector("[data-search-history-list]");
    this.historyContainer = this.querySelector(
      ".predictive-search-history-container",
    );
    this.clearHistoryButton = this.querySelector("[data-clear-history]");
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.mobileInitialRestored = true;

    this.setupEventListeners();
    this.renderHistory();
  }

  connectedCallback() {
    this.mobileViewportCleanup?.();
    const modal = this.closest('.search-modal');
    const details = this.closest('details-modal')?.querySelector('details');
    if (!modal || !details) return;
    const media = window.matchMedia('(max-width: 749px)');
    const scroller = modal.querySelector('.search-modal__content');
    let unlock = null;
    let start = null;
    const sync = () => {
      if (!details.open || !media.matches) {
        unlock?.();
        unlock = null;
        return;
      }
      if (unlock) return;
      const y = window.scrollY;
      const body = document.body;
      const properties = ['position', 'top', 'left', 'width'];
      const previous = properties.map(name => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)]);
      const ancestors = [];
      for (let node = modal.parentElement; node && node !== body; node = node.parentElement) {
        if (!node.classList.contains('ss-search-containing-ancestor')) {
          node.classList.add('ss-search-containing-ancestor');
          ancestors.push(node);
        }
      }
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `-${y}px`);
      body.style.setProperty('left', '0');
      body.style.setProperty('width', '100%');
      document.documentElement.classList.add('ss-mobile-search-open');
      unlock = () => {
        previous.forEach(([name, value, priority]) => {
          if (value) body.style.setProperty(name, value, priority);
          else body.style.removeProperty(name);
        });
        ancestors.forEach(node => node.classList.remove('ss-search-containing-ancestor'));
        document.documentElement.classList.remove('ss-mobile-search-open');
        window.scrollTo(0, y);
      };
    };
    const touchStart = event => {
      const touch = event.touches[0];
      start = touch ? { x: touch.clientX, y: touch.clientY } : null;
    };
    const touchMove = event => {
      if (!start || !media.matches || document.activeElement !== this.input) return;
      const touch = event.touches[0];
      if (!touch) return;
      const dy = start.y - touch.clientY;
      const dx = Math.abs(start.x - touch.clientX);
      if (dy > 24 && dy > dx * 1.3) {
        this.input.blur();
        start = null;
      }
    };
    details.addEventListener('toggle', sync);
    media.addEventListener('change', sync);
    scroller?.addEventListener('touchstart', touchStart, { passive: true });
    scroller?.addEventListener('touchmove', touchMove, { passive: true });
    sync();
    this.mobileViewportCleanup = () => {
      unlock?.();
      details.removeEventListener('toggle', sync);
      media.removeEventListener('change', sync);
      scroller?.removeEventListener('touchstart', touchStart);
      scroller?.removeEventListener('touchmove', touchMove);
    };
  }

  disconnectedCallback() {
    this.mobileViewportCleanup?.();
    this.mobileViewportCleanup = null;
    this.countAbortController?.abort();
    clearTimeout(this.countTimer);
    this.abortController.abort();
  }

  updateAllProducts() {
    if (!this.seeMoreContainer) return;
    const term = this.getQuery();
    if (term && this.countTerm === term && this.seeMoreContainer.firstElementChild) return;
    this.countTerm = term;
    this.countAbortController?.abort();
    clearTimeout(this.countTimer);
    if (!term) {
      this.seeMoreContainer.replaceChildren();
      return;
    }
    const destination = new URL(this.input.form.action, document.baseURI);
    destination.search = new URLSearchParams(new FormData(this.input.form)).toString();
    destination.searchParams.set('q', term);
    destination.searchParams.set('type', 'product');
    destination.searchParams.delete('view');
    const link = document.createElement('a');
    link.className = 'ss-search-all-products';
    link.textContent = 'Wyświetl wszystkie produkty';
    link.href = destination.href;
    link.addEventListener('click', () => this.saveSearchHistory(term));
    this.seeMoreContainer.replaceChildren(link);
    this.countCache ||= new Map();
    const key = destination.search;
    const applyCount = count => {
      if (this.getQuery() === term && link.isConnected && Number.isInteger(count) && count >= 0) {
        link.textContent = `Wyświetl wszystkie produkty (${count})`;
      }
    };
    if (this.countCache.has(key)) {
      applyCount(this.countCache.get(key));
      return;
    }
    this.countAbortController = new AbortController();
    const signal = this.countAbortController.signal;
    destination.searchParams.set('view', 'ss-count');
    (async () => {
      try {
        const response = await fetch(destination.href, { signal });
        if (!response.ok) return;
        const data = JSON.parse(await response.text());
        if (signal.aborted || this.getQuery() !== term || !Number.isInteger(data.count) || data.count < 0) return;
        if (this.countCache.size >= 30) this.countCache.delete(this.countCache.keys().next().value);
        this.countCache.set(key, data.count);
        applyCount(data.count);
      } catch (error) {
        // Keep the working full-search link without inventing a count.
      }
    })();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.input.addEventListener('input', () => {
      this.countAbortController?.abort();
      clearTimeout(this.countTimer);
      this.countTerm = null;
      this.seeMoreContainer?.replaceChildren();
      if (this.getQuery()) {
        this.countTimer = setTimeout(() => this.updateAllProducts(), 150);
      }
    });
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    if (this.clearHistoryButton) {
      this.clearHistoryButton.addEventListener(
        "click",
        this.clearHistory.bind(this),
      );
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.removeAttribute("results");
      this.closeResults(true);
      this.open();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (query.length) {
      this.saveSearchHistory(query);
    }
    if (!query.length || this.querySelector('[aria-selected="true"] a'))
      event.preventDefault();
  }

  saveSearchHistory(term) {
    if (!term) return;
    let history = JSON.parse(localStorage.getItem("search-history") || "[]");
    history = history.filter(
      (item) => item.toLowerCase() !== term.toLowerCase(),
    );
    history.unshift(term);
    history = history.slice(0, 5); // Keep last 5
    localStorage.setItem("search-history", JSON.stringify(history));
    this.renderHistory();
  }

  renderHistory() {
    if (!this.historyList) return;
    const history = JSON.parse(localStorage.getItem("search-history") || "[]");
    if (history.length === 0) {
      this.historyContainer.classList.add("hidden");
      return;
    }

    this.historyContainer.classList.remove("hidden");
    this.historyList.innerHTML = history
      .map(
        (term) => `
      <div class="predictive-search-history__item" data-history-term="${term}">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-clock"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
        <span>${term}</span>
      </div>
    `,
      )
      .join("");

    this.historyList
      .querySelectorAll(".predictive-search-history__item")
      .forEach((item) => {
        item.addEventListener("click", (e) => {
          const term = item.dataset.historyTerm;
          this.input.value = term;
          this.onChange();
        });
      });
  }

  clearHistory() {
    localStorage.removeItem("search-history");
    this.renderHistory();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.open();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (window.matchMedia('(max-width: 749px)').matches &&
          this.closest('details-modal')?.querySelector('details')?.open) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.removeAttribute("results");
      this.closeResults(true);
      this.open();
    }
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      try {
        const regex = new RegExp(previousTerm, "g");
        const matches = currentButtonText.match(regex);
        if (matches && matches.length > 1) {
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      } catch (e) {
        // Handle invalid regex if previousTerm has special chars
        searchForTextElement.innerText = currentButtonText
          .split(previousTerm)
          .join(newTerm);
      }
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    this.updateAllProducts();
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    this.searchRequestId = (this.searchRequestId || 0) + 1;
    const requestId = this.searchRequestId;
    const isCurrentRequest = () =>
      !signal.aborted &&
      requestId === this.searchRequestId &&
      this.getQuery() === searchTerm;

    if (!isCurrentRequest()) return;

    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      { signal },
    )
      .then((response) => {
        if (!isCurrentRequest()) return null;
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        // Check the live input too: it can change during the debounce delay.
        if (!isCurrentRequest() || text === null) return;

        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (
          !isCurrentRequest() ||
          error?.name === "AbortError" ||
          error?.code === 20
        ) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (window.matchMedia("(max-width: 989px)").matches) {
      this.mobileInitialRestored = false;
    }
    const doc = new DOMParser().parseFromString(resultsMarkup, "text/html");
    const products = Array.from(
      doc.querySelectorAll('li[id^="predictive-search-option-product"]'),
    );
    const suggestions = Array.from(
      doc.querySelectorAll(
        'li[id^="predictive-search-option-query"], li[id^="predictive-search-option-collection"]',
      ),
    );

    if (products.length > 0) {
      if (this.productsHeading)
        this.productsHeading.innerText = "Wyniki wyszukiwania";
      if (this.productsGrid) {
        let itemsHTML = products
          .map((li) => {
            const link = li.querySelector("a");
            const img = li.querySelector("img");
            const titleEl = li.querySelector(
              ".predictive-search__item-heading",
            );
            const priceEl = li.querySelector(".predictive-search-product-price") || li.querySelector(".price");

            const title = titleEl ? titleEl.innerText : "";
            const price = priceEl ? priceEl.innerHTML : "";
            const url = link ? link.getAttribute("href") : "#";
            const imgSrc = img ? img.getAttribute("src") : "";

            return `
          <a href="${url}" class="predictive-search-initial__product-card">
            <div class="predictive-search-initial__product-image">
              <img src="${imgSrc}" loading="lazy" width="100" height="130">
            </div>
            <div class="predictive-search-initial__product-info">
              <p class="predictive-search-initial__product-title">${this.highlightTerm(title, this.searchTerm)}</p>
              <div class="predictive-search-initial__product-price">${price}</div>
            </div>
          </a>
        `;
          })
          .join("");

        this.updateProductsGrid(itemsHTML);

        // The shared all-products link and count are managed separately.
      }
    } else {
      if (this.productsHeading)
        this.productsHeading.innerText = "No products found";
      if (this.productsGrid) this.productsGrid.innerHTML = "";
      // Keep the full-search link, including when predictive results are empty.
    }

    if (window.matchMedia('(max-width: 749px)').matches && !suggestions.length && this.categoriesList) {
      this.categoriesList.replaceChildren();
    }
    if (suggestions.length > 0) {
      if (this.categoriesHeading)
        this.categoriesHeading.innerText = "Sugerowane";
      if (this.categoriesList) {
        this.categoriesList.innerHTML = suggestions
          .map((li) => {
            const link = li.querySelector("a");
            const textEl = li.querySelector(
              ".predictive-search__item-query-result",
            );
            const text = textEl
              ? textEl.innerText
              : li.querySelector(".predictive-search__item-heading")?.innerText;
            const url = link ? link.getAttribute("href") : "#";
            return `<li class="predictive-search-initial__item predictive-search-initial__item--prediction"><a href="${url}">${text}</a></li>`;
          })
          .join("");
      }
    }

    this.setAttribute("results", true);
    this.setLiveRegionResults();
    this.open();
  }

  updateProductsGrid(itemsHTML) {
    // Keep the existing desktop rendering path unchanged.
    if (!window.matchMedia("(max-width: 989px)").matches) {
      this.productsGrid.innerHTML = itemsHTML;
      return;
    }

    const grid = this.productsGrid;
    const template = document.createElement("template");
    template.innerHTML = itemsHTML;
    const resourcePath = (url) => {
      try {
        return new URL(url, document.baseURI).pathname;
      } catch {
        return url || "";
      }
    };
    const productKey = (card) => resourcePath(card.getAttribute("href"));
    const previousCards = Array.from(grid.children);
    const existingCards = new Map(
      previousCards.map((card) => [productKey(card), card]),
    );
    const retainedCards = new Set();

    Array.from(template.content.children).forEach((incoming, index) => {
      const existing = existingCards.get(productKey(incoming));
      const card = existing || incoming;
      retainedCards.add(card);

      if (existing) {
        // Search tracking parameters can change without the product changing.
        existing.setAttribute("href", incoming.getAttribute("href"));
        for (const selector of [
          ".predictive-search-initial__product-title",
          ".predictive-search-initial__product-price",
        ]) {
          const currentPart = existing.querySelector(selector);
          const nextPart = incoming.querySelector(selector);
          if (currentPart && nextPart && currentPart.innerHTML !== nextPart.innerHTML) {
            currentPart.innerHTML = nextPart.innerHTML;
          }
        }

        const currentImage = existing.querySelector("img");
        const nextImage = incoming.querySelector("img");
        const nextSource = nextImage?.getAttribute("src");
        if (
          currentImage && nextSource &&
          resourcePath(currentImage.getAttribute("src")) !== resourcePath(nextSource)
        ) {
          currentImage.removeAttribute("srcset");
          currentImage.removeAttribute("sizes");
          currentImage.setAttribute("src", nextSource);
        }
      } else {
        // There are only a few suggestion cards; do not defer their new images.
        incoming.querySelector("img")?.setAttribute("loading", "eager");
      }

      // Do not detach unchanged cards or their already loaded images.
      if (grid.children[index] !== card) {
        grid.insertBefore(card, grid.children[index] || null);
      }
    });

    previousCards.forEach((card) => {
      if (!retainedCards.has(card)) card.remove();
    });
  }

  highlightTerm(text, term) {
    if (!term) return text;
    const cleanTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${cleanTerm})`, "gi");
    return text.replace(regex, "<mark>$1</mark>");
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const liveRegion = this.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );
    if (liveRegion) {
      this.setLiveRegionText(liveRegion.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.countAbortController?.abort();
      clearTimeout(this.countTimer);
      this.seeMoreContainer?.replaceChildren();
      this.input.value = "";
      this.removeAttribute("results");

      const mobile = window.matchMedia("(max-width: 989px)").matches;
      if (mobile) {
        this.abortController.abort();
        this.abortController = new AbortController();
        this.searchTerm = "";
      }
      // On mobile, restore once instead of rebuilding on every empty keyup.
      if (!mobile || !this.mobileInitialRestored) {
        if (this.productsGrid) {
          if (mobile) this.updateProductsGrid(this.initialProductsHTML);
          else this.productsGrid.innerHTML = this.initialProductsHTML;
        }
        if (this.categoriesList)
          this.categoriesList.innerHTML = this.initialCategoriesHTML;
        if (this.productsHeading)
          this.productsHeading.innerText = "Polecane produkty";
        if (this.categoriesHeading)
          this.categoriesHeading.innerText = "Polecane kategorie";
        if (this.seeMoreContainer) this.seeMoreContainer.innerHTML = "";
        if (mobile) this.mobileInitialRestored = true;
      }
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);